import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { type JSX, useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CreateOptionChip } from '~components/FormFields/CreateOptionChip';
import { TextFormField } from '~components/FormFields/TextFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { useSites } from '~hooks/useSites/useSites';
import { AddArrayItemBox } from '~pages/Sales/sharedSales/AddArrayItemBox';
import { FormArrayRowCard } from '~pages/Sales/sharedSales/FormArrayRowCard';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import {
  markMaterialRatesDeletedForSoftDeletedMaterialTotal,
  remapMaterialRatesAfterMaterialTotalRemoval,
} from '../../materialRatesFormUtils';
import {
  EmptyProjectMaterialTotalItem,
  type ProjectFormSchemaInterface,
} from '../../projectFormSchema';
import { ProjectMaterialRatesSection } from '../components/ProjectMaterialRatesSection';
import { getPhaseIdentityKey } from '../phaseIdentity';
import { useProjectFormV2Context } from '../ProjectFormV2Context';

// Mirrors the legacy form. Render-only by design: the panel
// owns useFieldArray for `projectMaterialTotals` and writes back through
// setValue / append / remove / update. The four reactive hooks
// (effects / watchers / flag reads) live in ProjectFormV2Provider — the
// panel-grep guard forbids them inside panel files. The
// PhaseDrawer mutates `phases`; this panel reads that list reactively
// through context.watchedPhases.
//
// `ProjectMaterialRatesSection` lives under projectFormV2/components/ —
// sibling to panels/, so it can own the per-row reactive subscriptions
// without tripping this file's grep guard. This split was planned for
// deliberately, anticipating that per-row reactive subscriptions would need
// to live outside the render-only panel.
export function MaterialsPanel(): JSX.Element {
  const { control, formState, getValues, setValue } =
    useFormContext<ProjectFormSchemaInterface>();
  const { flags, watchedSelectedCompany, watchedPhases, defaultProject } =
    useProjectFormV2Context();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const { createMaterialByName, getMaterialsByCompanyIdTypeahead } = useMaterials();
  const { getCompanySitesTypeahead } = useSites();
  const { toasterStore } = useStores();

  // Feeds the "Add new '<name>'" chip in each row's noOptionsText. One
  // shared input-text/loading pair covers all rows, like the legacy form —
  // only one autocomplete popper is open at a time.
  const [tmpMaterial, setTmpMaterial] = useState('');
  const [isMaterialLoading, setIsMaterialLoading] = useState(false);
  const onInputMaterial = useCallback(
    (_event: React.SyntheticEvent, value: string) => setTmpMaterial(value),
    [],
  );

  // Mirrors the legacy form, going through the typed
  // createMaterialByName (the legacy `as unknown as Material` shortcut is
  // forbidden by the v2 grep gate). The leaf-path setValue marks the row
  // dirty so the sparse PATCH (deparseUpdateV2) ships it. Failures already
  // toast via connection.handleRequestError inside the hook.
  const createMaterial = useCallback(
    async (index: number) => {
      const companyId = watchedSelectedCompany?.id || defaultProject?.company?.id;
      if (!companyId) {
        toasterStore.push(
          alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
        );
        return;
      }

      try {
        setIsMaterialLoading(true);
        const material = await createMaterialByName({
          companyId,
          name: tmpMaterial,
        });

        toasterStore.push(
          alert(
            t('administration.material.material_created', { name: material.name }),
            AlertTypes.success,
          ),
        );
        setValue(`projectMaterialTotals.${index}.material`, material, {
          shouldDirty: true,
        });
      } catch (error) {
        void error;
      } finally {
        setIsMaterialLoading(false);
      }
    },
    [
      createMaterialByName,
      defaultProject?.company?.id,
      setValue,
      tmpMaterial,
      toasterStore,
      watchedSelectedCompany?.id,
    ],
  );

  const {
    fields: materialTotalsFields,
    append: appendMaterialTotal,
    remove: removeMaterialTotal,
    update: updateMaterialTotal,
  } = useFieldArray({ control, name: 'projectMaterialTotals' });

  const activePhases = (watchedPhases ?? []).filter((phase) => !phase?.isDeleted);

  const handleRemove = (index: number) => {
    // Read schema row from form state (not from useFieldArray.fields which
    // augments rows with an RHF-internal id under the same key). The legacy
    // form follows the same pattern.
    const row = getValues(`projectMaterialTotals.${index}`);
    const currentRates = getValues('materialRates') ?? [];
    if (!row?.id) {
      // Brand-new row, not persisted yet — drop it from the array, then
      // re-key associated materialRates so any rate that referenced this
      // row by parentIndex is dropped (or kept as a deleted tombstone if
      // it was saved) and rates after this index are decremented to keep
      // parentIndex aligned with the new positions. Mirrors the legacy form.
      removeMaterialTotal(index);
      const nextRates = remapMaterialRatesAfterMaterialTotalRemoval(currentRates, index);
      setValue(
        'materialRates',
        nextRates as ProjectFormSchemaInterface['materialRates'],
        { shouldDirty: true },
      );
    } else {
      // Saved row — keep it in the array but mark it deleted so the PATCH
      // sends destroy:1 alongside the canonical row id; cascade the soft
      // delete to associated materialRates so the rates dispatch on submit
      // emits a DELETE for each persisted rate that pointed at this row.
      // Mirrors the legacy form.
      updateMaterialTotal(index, { ...row, isDeleted: true });
      const nextRates = markMaterialRatesDeletedForSoftDeletedMaterialTotal(
        currentRates,
        index,
      );
      setValue(
        'materialRates',
        nextRates as ProjectFormSchemaInterface['materialRates'],
        { shouldDirty: true },
      );
    }
  };

  return (
    <Card sx={{ p: 2, mb: 2, overflow: 'visible' }}>
      <Typography variant="form-section-title" sx={{ mb: 0 }}>
        {t('form_fields.materials')}
      </Typography>

      {materialTotalsFields.map((materialTotal, index) =>
        materialTotal.isDeleted ? null : (
          <FormArrayRowCard
            key={materialTotal.id}
            dataTestId={`project-form-v2-materials-row-${index}`}
            onRemove={() => handleRemove(index)}
            removeIconDataTestId="project-form-v2-materials-remove"
          >
            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                control={control}
                errors={formState.errors}
                name={`projectMaterialTotals.${index}.material`}
                label={t('form_fields.material')}
                clearable={true}
                // Clearing a required material flags the field via the red
                // outline only; the raw yup message is not shown to the user
                // (TRE-15163), matching the sibling Unit/Phase fields.
                hideErrorMessage={true}
                getValue={(item) => item?.id}
                getLabel={(item) => item?.name || ''}
                asyncCallback={getMaterialsByCompanyIdTypeahead}
                extraRequestOptions={{
                  companyId: watchedSelectedCompany?.id || defaultProject?.company?.id,
                }}
                onInput={onInputMaterial}
                noOptionsText={
                  tmpMaterial ? (
                    <CreateOptionChip
                      data-test-id="project-form-v2-material-create-chip"
                      disabled={isMaterialLoading}
                      name={tmpMaterial}
                      onClick={() => void createMaterial(index)}
                    />
                  ) : (
                    t('form_fields.no_options')
                  )
                }
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <TextFormField
                control={control}
                errors={formState.errors}
                label={t('project.form.total_quantity')}
                name={`projectMaterialTotals.${index}.totalQuantity`}
                type="number"
              />
            </Grid>

            <Grid size={{ xs: 3 }}>
              <Box data-test-id="project-form-v2-materials-unit">
                <AutocompleteFormField
                  clearable={true}
                  control={control}
                  errors={formState.errors}
                  getLabel={(item) => item?.name || ''}
                  getValue={(item) => item?.id}
                  label={t('form_fields.unit')}
                  list={unitOfMeasureOptions}
                  name={`projectMaterialTotals.${index}.unitOfMeasure`}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <AutocompleteFormField
                clearable={true}
                control={control}
                disabled={!activePhases.length}
                errors={formState.errors}
                getLabel={(item: { name?: string; code?: string }) =>
                  item.code ? `${item.name} (${item.code})` : item.name || ''
                }
                getValue={(item: {
                  id?: string | null;
                  name?: string | null;
                  code?: string | null;
                }) => getPhaseIdentityKey(item) ?? ''}
                label={t('form_fields.phase_name')}
                list={activePhases}
                name={`projectMaterialTotals.${index}.phase`}
                placeholder={
                  !activePhases.length ? t('project.form.add_phases_first') : undefined
                }
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                clearable={true}
                control={control}
                errors={formState.errors}
                getLabel={(item) => item?.name || ''}
                getValue={(item) => item?.id}
                isRequired={flags.fastWeighEnabled}
                label={t('form_fields.pick_up_site')}
                name={`projectMaterialTotals.${index}.site`}
                asyncCallback={getCompanySitesTypeahead}
                extraRequestOptions={{
                  companyId: watchedSelectedCompany?.id || defaultProject?.company?.id,
                  // Active sites only, matching the order form's own pickup
                  // selector. An inactive site saved here could never be
                  // matched by a pickup, so the pair would never resolve.
                  onlyActiveSites: true,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <ProjectMaterialRatesSection parentIndex={index} />
            </Grid>
          </FormArrayRowCard>
        ),
      )}

      <AddArrayItemBox
        title={t('project.form.add_material')}
        onAdd={() => appendMaterialTotal(EmptyProjectMaterialTotalItem)}
        dataTestId="project-form-v2-materials-add"
      />

      <Box data-test-id="project-form-v2-material-rate-itemized">
        <CheckboxInput
          control={control}
          label={t('form_fields.itemize_material_rate')}
          name="materialRateItemized"
          sx={{
            [`& .${formControlLabelClasses.root}`]: { mr: 0 },
            [`& .${checkboxClasses.root}`]: { pr: '2px' },
            [`& .${typographyClasses.root}`]: {
              fontSize: '12px',
              fontWeight: 400,
            },
          }}
        />
      </Box>
    </Card>
  );
}
